import { memo, PropsWithChildren, useCallback, useLayoutEffect, useRef, useState } from "react";
import { clsx } from "clsx";
import "./WithSideAds.scss";
import { useExperiment } from "@/common/hooks/useExperiment";
import { useStickySection } from "@/common/hooks/useStickySection";
import useWindowDimensions from "@/common/hooks/useWindowDimension";
import { EXPERIMENT_NO_ADS } from "@/common/services/experiment/constants";
import { OnlyFansCreatorsSidebar } from "./OnlyFansCreatorsSidebar";

const ADS_WIDTH = 466;
const STICKY_ACTIVATE_OFFSET = 0;
const STICKY_DEACTIVATE_OFFSET = 60;

type WithSideAdsProps = PropsWithChildren;

export const WithSideAds = memo((props: WithSideAdsProps) => {
  const { children } = props;
  const { variant: noAds } = useExperiment({ key: EXPERIMENT_NO_ADS, fallback: false });
  const isDesktop = useWindowDimensions();
  const [showAds, setShowAds] = useState<boolean>(false);
  const [isClosed, setClosed] = useState<boolean>(false);
  const contentRef = useRef<HTMLDivElement | null>(null);
  const ref = useRef<HTMLDivElement | null>(null);

  const railMounted = !noAds && showAds;

  const { isSticky, sentinelRef } = useStickySection({
    activateOffset: STICKY_ACTIVATE_OFFSET,
    deactivateOffset: STICKY_DEACTIVATE_OFFSET,
    enabled: railMounted,
  });

  const close = useCallback(() => setClosed(true), []);
  const isStuck = isSticky && !isClosed;

  const updateShowAds = useCallback(() => {
    const containerWidth = ref.current?.clientWidth ?? 0;
    // Middle can be forced to 100% width when it's the only child; prefer its first child's width.
    const baseContentWidth =
      contentRef.current?.firstElementChild instanceof HTMLElement
        ? contentRef.current.firstElementChild.clientWidth
        : undefined;

    const contentWidth = baseContentWidth ?? contentRef.current?.clientWidth ?? 0;

    if (!isDesktop || containerWidth === 0 || contentWidth === 0) {
      setShowAds(false);
      return;
    }

    setShowAds(containerWidth >= contentWidth + ADS_WIDTH);
  }, [isDesktop]);

  useLayoutEffect(() => {
    updateShowAds();

    const observer = new ResizeObserver(updateShowAds);

    if (ref.current) {
      observer.observe(ref.current);
    }

    if (contentRef.current) {
      observer.observe(contentRef.current);
    }

    return () => observer.disconnect();
  }, [updateShowAds]);

  return (
    <div className="skyWrapper" ref={ref}>
      {railMounted && <div className="left side"></div>}

      <div className="middle" ref={contentRef}>
        {children}
      </div>

      {railMounted && <div className="right side">
        <div ref={sentinelRef} className="skyscraperSentinel" />
        <div className={clsx("skyscraper", !isClosed && "skyscraper_sticky", isStuck && "skyscraper_stuck")}>
          <OnlyFansCreatorsSidebar isStuck={isStuck} onClose={close} />
        </div>
      </div>}
    </div>
  );
});
